import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { routeTree } from "./routeTree.gen"
import "./styles/globals.css"
import { initAuth } from "./lib/auth/auth-store"
import { installAuthHubListeners } from "./lib/auth/install-auth-hub-listeners"
import { initializeInteractionSounds } from "./lib/interaction-sounds"

// Shared stale time for queries and router preloading
const STALE_TIME_MS = 1000 * 60 * 5 // 5 minutes

// Create a QueryClient
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: STALE_TIME_MS,
			gcTime: 1000 * 60 * 10, // 10 minutes (cache retention)
			retry: 1,
			refetchOnWindowFocus: false, // Don't refetch when window regains focus
		},
	},
})

// Create the router
const router = createRouter({
	routeTree,
	context: {
		queryClient,
	},
	defaultPreload: "intent",
	// Match query staleTime so preloaded data isn't immediately refetched on mount
	defaultPreloadStaleTime: STALE_TIME_MS,
	defaultViewTransition: true,
})

// Register router for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router
	}
}

async function bootstrap(): Promise<void> {
	await initAuth()
	const rootElement = document.getElementById("root")
	if (!rootElement) {
		throw new Error("Root element not found")
	}

	const root = createRoot(rootElement)
	root.render(
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</StrictMode>,
	)
}

function reportBootstrapFailure(error: unknown): void {
	console.error("[Portal] Failed to initialize application", error)
	const rootElement = document.getElementById("root")
	if (rootElement !== null) {
		rootElement.textContent = "The portal could not be started. Please refresh the page or contact support."
	}
}

installAuthHubListeners(queryClient)
initializeInteractionSounds()
void bootstrap().catch(reportBootstrapFailure)
